import axios from "axios";
import {
  removeLocalStorageItem,
} from "../utils/safeLocalStorage";
import { getLoginHref } from "../utils/appPaths";
import {
  clearSession,
  extendSessionFromServer,
  getSessionExpiresAt,
} from "../utils/auth";
import {
  applyClientLogHeaders,
  isApiClientLogEnabled,
  isFrontEndApiFailureLogEnabled,
  isFrontEndApiSuccessLogEnabled,
  logIncomingApiFailure,
  logIncomingApiSuccess,
  logOutgoingApiRequest,
  logSessionEvent,
} from "./apiClientLog";
import {
  API_LONG_RUNNING_TIMEOUT_MS,
  API_REQUEST_TIMEOUT_MS,
  API_UPLOAD_TIMEOUT_MS,
} from "../config/apiTimeouts";
import { recordApiFailure, recordApiSuccess } from "./apiConnectivity";

let lastUnauthorizedNotifyAt = 0;
const UNAUTHORIZED_NOTIFY_COOLDOWN_MS = 3000;

/** Extra console rows (→ / ← / ×) follow config/csrp-logging.json flags. */
const isApiDebugLogEnabled = () =>
  isFrontEndApiSuccessLogEnabled() || isFrontEndApiFailureLogEnabled();

const apiDebugLog = (direction, payload) => {
  if (!isApiDebugLogEnabled()) return;
  const ts = new Date().toISOString();
  // eslint-disable-next-line no-console
  console.info(`[CSRP API ${direction} ${ts}]`, payload);
};

const newCorrelationId = () => {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
  } catch {
    // ignore
  }
  return `req_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
};

const describeRequestUrl = (config) => {
  try {
    const built = axios.getUri({
      ...config,
      baseURL: config.baseURL || getApiBaseUrl(),
    });
    if (built) return built;
  } catch {
    // ignore
  }
  const base = String(config.baseURL || getApiBaseUrl() || "");
  const rel = String(config.url || "");
  return `${base.replace(/\/+$/, "")}/${rel.replace(/^\/+/, "")}`;
};

const shouldTrackApiCall = () => isApiDebugLogEnabled() || isApiClientLogEnabled();

const isAuthExemptRequest = (config) => {
  if (config?.__skipSessionGuard) return true;
  const rel = String(config?.url || "").toLowerCase();
  return (
    rel.includes("/api/auth/login") ||
    rel.includes("/api/auth/unauthorized") ||
    rel.includes("/api/health/ping")
  );
};

const notifyUnauthorized = () => {
  const now = Date.now();
  if (now - lastUnauthorizedNotifyAt <= UNAUTHORIZED_NOTIFY_COOLDOWN_MS) return;
  lastUnauthorizedNotifyAt = now;
  try {
    window.dispatchEvent(
      new CustomEvent("csrp:unauthorized", {
        detail: { loginHref: getLoginHref() },
      })
    );
    window.dispatchEvent(new CustomEvent("csrp:session-expired"));
  } catch {
    // no-op
  }
};

const trimTrailingSlash = (s) => String(s || "").replace(/\/+$/, "");

/** Dev API port (Kestrel `http` profile in launchSettings.json). Override with REACT_APP_LOCAL_API_PORT. */
const LOCAL_API_PORT = String(
  process.env.REACT_APP_LOCAL_API_PORT || "5001"
).replace(/[^0-9]/g, "") || "5001";

/** Dev API HTTPS port (Kestrel `https` profile). Override with REACT_APP_LOCAL_API_HTTPS_PORT. */
const LOCAL_API_HTTPS_PORT = String(
  process.env.REACT_APP_LOCAL_API_HTTPS_PORT || "7049"
).replace(/[^0-9]/g, "") || "7049";

/**
 * Local dev API origin. Prefer same hostname as the SPA (localhost vs 127.0.0.1) to avoid cross-origin quirks.
 * Override fully with REACT_APP_LOCAL_API_ORIGIN or REACT_APP_API_BASE_URL.
 */
function getDefaultDevApiOrigin() {
  const fromEnv = trimTrailingSlash(process.env.REACT_APP_LOCAL_API_ORIGIN);
  if (fromEnv) return fromEnv;
  if (typeof window === "undefined") {
    return `http://localhost:${LOCAL_API_PORT}`;
  }
  const { hostname, protocol } = window.location;
  if (hostname === "localhost" || hostname === "127.0.0.1") {
    // Avoid mixed-content: if UI is https, call API via https port (launchSettings.json uses 7049 by default).
    if (protocol === "https:") {
      return `https://${hostname}:${LOCAL_API_HTTPS_PORT}`;
    }
    return `http://${hostname}:${LOCAL_API_PORT}`;
  }
  const scheme = protocol === "https:" ? "https" : "http";
  // e.g. open UI as http://192.168.x.x:3000 → call API on same host
  return `${scheme}://${hostname}:${LOCAL_API_PORT}`;
}

/**
 * Base URL for API calls (axios + manual URLs).
 *
 * Local dev: same hostname as the page + port `REACT_APP_LOCAL_API_PORT` (default 5000), e.g. `http://localhost:5000`
 * when the app is on `http://localhost:3000`. Override origin with `REACT_APP_LOCAL_API_ORIGIN` or `REACT_APP_API_BASE_URL`.
 *
 * Optional dev proxy: `REACT_APP_USE_DEV_PROXY=true` + `src/setupProxy.js` (`REACT_APP_PROXY_TARGET` for IIS Express).
 */
export const getApiBaseUrl = () => {
  const UAT_API_BASE_URL = "https://dev.gobizmo.in/csrp_api";

  const fromEnv = trimTrailingSlash(process.env.REACT_APP_API_BASE_URL);
  if (fromEnv) return fromEnv;

  if (typeof window === "undefined") {
    return UAT_API_BASE_URL;
  }

  const host = window.location.hostname;
  const isLoopback = host === "localhost" || host === "127.0.0.1";
  const isDev = process.env.NODE_ENV === "development";
  const privateLan =
    /^192\.168\.\d{1,3}\.\d{1,3}$/.test(host) ||
    /^10\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(host);

  if (isDev && isLoopback && process.env.REACT_APP_USE_DEV_PROXY === "true") {
    return "";
  }

  if (
    isLoopback ||
    window.location.port === "3000" ||
    (isDev && privateLan)
  ) {
    return getDefaultDevApiOrigin();
  }

  // Deployed: API is hosted beside the SPA on the same domain (e.g. https://<host>/csrp_api), so the
  // SameSite=Strict session cookie and the CSP connect-src 'self' work on any UAT / Production domain.
  const apiPath = process.env.REACT_APP_API_PATH || "/csrp_api";
  return `${window.location.origin}${apiPath}`;
};

export {
  API_LONG_RUNNING_TIMEOUT_MS,
  API_REQUEST_TIMEOUT_MS,
  API_UPLOAD_TIMEOUT_MS,
} from "../config/apiTimeouts";

// Create axios instance (baseURL set per request so dev proxy / env changes always apply)
const api = axios.create({
  timeout: API_REQUEST_TIMEOUT_MS,
  withCredentials: true, // cookie-based session
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor: resolve API base + auth
api.interceptors.request.use(
  (config) => {
    config.baseURL = getApiBaseUrl();

    if (config.timeout == null) {
      config.timeout = config.__longRunning
        ? API_LONG_RUNNING_TIMEOUT_MS
        : API_REQUEST_TIMEOUT_MS;
    }

    applyClientLogHeaders(config);

    if (shouldTrackApiCall() && !config.__skipClientLog) {
      const id = newCorrelationId();
      config.headers = config.headers || {};
      config.headers["X-CSRP-Request-Id"] = id;
      config.__csrpReqId = id;
      config.__csrpReqStart =
        typeof performance !== "undefined" && typeof performance.now === "function"
          ? performance.now()
          : Date.now();
    }

    logOutgoingApiRequest(config, describeRequestUrl);

    if (!isAuthExemptRequest(config)) {
      const expiresAt = getSessionExpiresAt();
      if (expiresAt != null && Date.now() > expiresAt) {
        apiDebugLog("BLOCKED", {
          reason: "client_session_expired",
          url: describeRequestUrl(config),
          expiresAt,
          now: Date.now(),
        });
        const err = new Error("Session expired (client guard)");
        err.isSessionExpired = true;
        err.config = config;
        logSessionEvent("session-end", "client_session_expired", getApiBaseUrl);
        removeLocalStorageItem("token");
        removeLocalStorageItem("user");
        removeLocalStorageItem("roles");
        clearSession();
        notifyUnauthorized();

        return Promise.reject(err);
      }
    }

    // Checklist question APIs: disable caching end-to-end (browser / proxies).
    const rel = String(config.url || "").toLowerCase();
    if (rel.includes("/api/checklistquestion")) {
      config.headers = config.headers || {};
      config.headers["Cache-Control"] =
        "no-store, no-cache, max-age=0, must-revalidate";
      config.headers.Pragma = "no-cache";
      config.headers.Expires = "0";
      const method = String(config.method || "get").toLowerCase();
      if (method === "get" || method === "head") {
        const ts = Date.now();
        if (config.params == null) {
          config.params = { _nocache: ts };
        } else if (
          typeof config.params === "object" &&
          !Array.isArray(config.params) &&
          !(typeof URLSearchParams !== "undefined" &&
            config.params instanceof URLSearchParams)
        ) {
          config.params = { ...config.params, _nocache: ts };
        } else if (
          typeof URLSearchParams !== "undefined" &&
          config.params instanceof URLSearchParams
        ) {
          config.params.set("_nocache", String(ts));
        }
      }
    }

    if (isApiDebugLogEnabled() && config.__csrpReqId && isFrontEndApiSuccessLogEnabled()) {
      apiDebugLog("→", {
        id: config.__csrpReqId,
        method: String(config.method || "get").toUpperCase(),
        url: describeRequestUrl(config),
      });
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => {
    const cfg = response.config || {};
    if (cfg.__csrpReqId && isFrontEndApiSuccessLogEnabled()) {
      const t0 = cfg.__csrpReqStart;
      const ms =
        typeof performance !== "undefined" && typeof performance.now === "function" && t0 != null
          ? Math.round(performance.now() - t0)
          : null;
      apiDebugLog("←", {
        id: cfg.__csrpReqId,
        status: response.status,
        ms,
        url: describeRequestUrl(cfg),
      });
    }
    const ms =
      typeof performance !== "undefined" && typeof performance.now === "function" &&
      cfg.__csrpReqStart != null
        ? Math.round(performance.now() - cfg.__csrpReqStart)
        : null;
    logIncomingApiSuccess(response, describeRequestUrl, ms, getApiBaseUrl);
    recordApiSuccess(response.config);
    const url = String(response.config?.url || "").toLowerCase();
    if (url.includes("/api/session/keepalive") && response.data) {
      const body = response.data?.data ?? response.data?.Data ?? response.data;
      const sec = body?.sessionExpiresIn ?? body?.SessionExpiresIn;
      if (typeof sec === "number") {
        extendSessionFromServer(sec);
      }
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config || {};
    if (originalRequest.__csrpReqId && isFrontEndApiFailureLogEnabled()) {
      const t0 = originalRequest.__csrpReqStart;
      const ms =
        typeof performance !== "undefined" && typeof performance.now === "function" && t0 != null
          ? Math.round(performance.now() - t0)
          : null;
      apiDebugLog("×", {
        id: originalRequest.__csrpReqId,
        ms,
        url: describeRequestUrl(originalRequest),
        message: error.message,
        code: error.code,
        isSessionExpired: error.isSessionExpired === true,
        status: error.response?.status,
        isTimeout: error.isTimeout === true,
        isUnauthorized: error.isUnauthorized === true,
        isForbidden: error.isForbidden === true,
      });
    }

    const failMs =
      typeof performance !== "undefined" && typeof performance.now === "function" &&
      originalRequest.__csrpReqStart != null
        ? Math.round(performance.now() - originalRequest.__csrpReqStart)
        : null;

    if (!originalRequest.__skipClientFailureReport) {
      logIncomingApiFailure(error, describeRequestUrl, failMs, getApiBaseUrl);
    } else {
      logIncomingApiFailure(error, describeRequestUrl, failMs, null);
    }

    // Retry transient network errors for safe/idempotent methods.
    const method = String(originalRequest.method || "get").toLowerCase();
    const isIdempotent = method === "get" || method === "head" || method === "options";
    const isCanceled =
      error.code === "ERR_CANCELED" ||
      error.name === "CanceledError" ||
      error.name === "AbortError" ||
      error.__CANCEL__ === true;
    const isTimeout =
      (error.code === "ECONNABORTED" && error.message?.includes("timeout")) ||
      error.isTimeout === true;
    const isNetwork =
      !isCanceled &&
      (error.code === "ERR_NETWORK" ||
        error.message?.includes("Network Error") ||
        error.response == null);

    // Cancelled requests are intentional (navigate away / abort dashboard) — do not retry or alarm.
    if (isCanceled) {
      return Promise.reject(error);
    }

    if (isIdempotent && (isTimeout || isNetwork) && !originalRequest.__csrpRetried) {
      originalRequest.__csrpRetried = true;
      await new Promise((r) => setTimeout(r, 600));
      return api.request(originalRequest);
    }

    const status = error.response?.status;
    if (status === 401 || status === 403) {
      if (!isAuthExemptRequest(originalRequest)) {
        removeLocalStorageItem("token");
        removeLocalStorageItem("user");
        removeLocalStorageItem("roles");
        clearSession();
        notifyUnauthorized();
      }

      error.isUnauthorized = status === 401;
      error.isForbidden = status === 403;
    }
    // Surface client-side timeouts distinctly (axios: ECONNABORTED; some browsers: ERR_NETWORK)
    if (error.code === "ECONNABORTED" && error.message?.includes("timeout")) {
      error.isTimeout = true;
    }
    recordApiFailure(error, originalRequest);
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: async (username, password) => {
    try {
      const base = getApiBaseUrl();
      console.log(
        "Attempting login:",
        `${base || (typeof window !== "undefined" ? window.location.origin : "")}/api/auth/login`
      );

      const response = await api.post("/api/auth/login", {
        userName: username,
        password: password || "", // Password is optional for now, send empty string instead of null
      });
      console.log("Login successful");
      return response.data;
    } catch (error) {
      console.error("Login failed:", {
        code: error.code,
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
      });
      throw error;
    }
  },
  // After Microsoft Entra ID sign-in (MFA) redirects back, fetch the same payload as a normal login.
  completeAzureLogin: async () => {
    const response = await api.get("/api/auth/azure/complete");
    return response.data;
  },
  logout: async () => {
    const response = await api.post("/api/auth/logout");
    return response.data;
  },
  getUser: async (userIdCode) => {
    const response = await api.get(`/api/auth/user/${userIdCode}`);
    return response.data;
  },
};

// Complaint API
export const complaintAPI = {
  getAll: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.userId) queryParams.append("userId", params.userId);
    if (params.statusId) queryParams.append("statusId", params.statusId);
    if (params.complaintTypeId)
      queryParams.append("complaintTypeId", params.complaintTypeId);
    if (params.verticalId) queryParams.append("verticalId", params.verticalId);
    if (params.territoryId)
      queryParams.append("territoryId", params.territoryId);
    if (params.fromDate) queryParams.append("fromDate", params.fromDate);
    if (params.toDate) queryParams.append("toDate", params.toDate);
    if (params.searchText) queryParams.append("searchText", params.searchText);
    queryParams.append("pageNumber", params.pageNumber || 1);
    queryParams.append("pageSize", params.pageSize || 50);

    const response = await api.get(`/api/complaints?${queryParams}`);
    return response.data;
  },
  getById: async (id) => {
    const response = await api.get(`/api/complaints/${id}`);
    return response.data;
  },
  create: async (data) => {
    const response = await api.post("/api/complaints", data);
    return response.data;
  },
  updateStatus: async (id, statusId, comments) => {
    const response = await api.put(`/api/complaints/${id}/status`, {
      statusId,
      comments,
    });
    return response.data;
  },
  getInvoiceDetails: async (invoiceNumber, market, complaintTypeId) => {
    const params = {
      invoiceNumber,
      market,
    };
    if (complaintTypeId) {
      params.complaintTypeId = complaintTypeId;
    }

    const response = await api.get("/api/complaints/invoice-details", {
      params,
    });
    return response.data;
  },
  saveChecklistDraft: async (data) => {
    const response = await api.post("/api/complaints/checklist-draft", data);
    return response.data;
  },
  getDrafts: async (userId, pageNumber = 1, pageSize = 50) => {
    const response = await api.get(
      `/api/complaints/drafts?userId=${userId}&pageNumber=${pageNumber}&pageSize=${pageSize}`
    );
    return response.data;
  },
  getDraftById: async (draftId) => {
    const response = await api.get(`/api/complaints/drafts/${draftId}`);
    return response.data;
  },
  deleteDraft: async (draftId, userId = 1) => {
    const response = await api.delete(
      `/api/complaints/drafts/${draftId}?userId=${userId}`
    );
    return response.data;
  },
  getPreviousComplaintsByInvoice: async (invoiceNumber, userIdCode = null, options = {}) => {
    const qp = new URLSearchParams();
    qp.append("invoiceNumber", String(invoiceNumber ?? ""));
    // View page: all SRs for invoice (approver/closer/initiator). Raise: filter by creator.
    if (options?.forView === true) {
      qp.append("forView", "true");
    } else if (userIdCode && String(userIdCode).trim()) {
      qp.append("userIdCode", String(userIdCode).trim());
    }
    const response = await api.get(`/api/complaints/previous-complaints?${qp.toString()}`);
    return response.data;
  },
  /** Get end consumer by mobile number (for raise-complaint). Returns { success, data } where data is null if not found. */
  getEndConsumerByMobile: async (mobile) => {
    const trimmed = (mobile || "").trim();
    if (!trimmed) return { success: true, data: null };
    const response = await api.get("/api/complaints/end-consumer-by-mobile", {
      params: { mobile: trimmed },
    });
    return response.data;
  },
  /**
   * Upload one invoice attachment (PDF, JPG, PNG). Saves with unique name on server.
   * copyFor: 'Invoice Copy' | 'Secondary Invoice' (optional, for backend/draft).
   * Returns { data: { filePath, fileName, fileSize, fileType, copyFor } }.
   */
  uploadInvoiceAttachment: async (file, copyFor = "") => {
    const formData = new FormData();
    formData.append("file", file);
    if (copyFor) formData.append("copyFor", copyFor);
    const response = await api.post("/api/complaints/upload-invoice-attachment", formData, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: API_UPLOAD_TIMEOUT_MS,
    });
    return response.data;
  },
  /** Upload checklist attachment (damage image or video). type: 'DamageImage' | 'Video'. Returns { filePath, fileName, fileSize, fileType }. */
  uploadChecklistAttachment: async (file, type = "DamageImage") => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("type", type);
    const response = await api.post("/api/complaints/upload-checklist-attachment", formData, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: API_UPLOAD_TIMEOUT_MS,
    });
    return response.data;
  },
  saveComplaintDraft: async (data) => {
    const response = await api.post("/api/complaints/draft", data);
    return response.data;
  },
  getComplaintDrafts: async (createdBy = null, pageNumber = 1, pageSize = 50) => {
    let url = `/api/complaints/drafts-list?pageNumber=${pageNumber}&pageSize=${pageSize}`;
    if (createdBy) {
      url += `&createdBy=${encodeURIComponent(createdBy)}`;
    }
    const response = await api.get(url);
    return response.data;
  },
  /**
   * Get full draft by SRCode (e.g. SRD000001) for loading on raise-complaint page.
   * Returns { success, data, message }. data is null if invalid/not found.
   */
  getDraftByCode: async (srCode) => {
    if (!srCode || !String(srCode).trim()) {
      return { success: false, data: null, message: "srCode is required" };
    }
    const response = await api.get("/api/complaints/draft-by-code", {
      params: { srCode: String(srCode).trim() },
    });
    return response.data;
  },
  /**
   * Delete a complaint draft by SRCode (only when status is Draft).
   * Returns { success, data, message }.
   */
  deleteDraftByCode: async (srCode) => {
    if (!srCode || !String(srCode).trim()) {
      return { success: false, data: null, message: "srCode is required" };
    }
    const response = await api.delete("/api/complaints/draft-by-code", {
      params: { srCode: String(srCode).trim() },
    });
    return response.data;
  },
  /**
   * Submit final SR (same API pattern as other complaint calls).
   * Payload: { srCode?, floorFormJson?, submission? }. Returns { success, data: { parentSRNumber }, message }.
   */
  submitFinal: async (payload) => {
    // Long-running: SP next-approver lookup can be slow; admin email is fire-and-forget on failure.
    const response = await api.post("/api/complaints/submit-final", payload, {
      __longRunning: true,
      timeout: API_LONG_RUNNING_TIMEOUT_MS,
    });
    return response.data;
  },

  validateInitiatorInvoiceGeo: async (invoiceOrOdnNo, plantCodesCsv = null) => {
    const queryParams = new URLSearchParams();
    queryParams.append("invoiceOrOdnNo", String(invoiceOrOdnNo ?? "").trim());
    if (plantCodesCsv) queryParams.append("plantCodesCsv", String(plantCodesCsv).trim());
    const response = await api.get(
      `/api/complaints/validate-initiator-invoice-geo?${queryParams.toString()}`
    );
    return response.data;
  },

  /**
   * Get paged list of Service Requests (SR* tables) for list views (Complaints/BCM SR list).
   * Params: { createdBy?, statusCode?, fromDate?, toDate?, searchText?, pageNumber?, pageSize? }
   * Returns { success, data: { items, totalRecords, pageNumber, pageSize }, message }.
   */
  getServiceRequestList: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.createdBy) queryParams.append("createdBy", params.createdBy);
    if (params.approverUserId)
      queryParams.append("approverUserId", params.approverUserId);
    if (params.statusCode) queryParams.append("statusCode", params.statusCode);
    if (params.statusCodesCsv)
      queryParams.append("statusCodesCsv", params.statusCodesCsv);
    if (params.businessVerticalCodesCsv)
      queryParams.append(
        "businessVerticalCodesCsv",
        params.businessVerticalCodesCsv
      );
    if (params.customerCode) queryParams.append("customerCode", params.customerCode);
    if (params.srCategory) queryParams.append("srCategory", params.srCategory);
    if (params.category) queryParams.append("category", params.category);
    if (params.fromDate) queryParams.append("fromDate", params.fromDate);
    if (params.toDate) queryParams.append("toDate", params.toDate);
    if (params.searchText) queryParams.append("searchText", params.searchText);
    if (params.queryToRespond === true || String(params.queryToRespond).toLowerCase() === "true") {
      queryParams.append("queryToRespond", "true");
    }
    if (params.specialCase === true || String(params.specialCase).toLowerCase() === "true") {
      queryParams.append("specialCase", "true");
    }
    queryParams.append("pageNumber", params.pageNumber || 1);
    queryParams.append("pageSize", params.pageSize || 50);

    const response = await api.get(`/api/complaints/sr-list?${queryParams}`);
    return response.data;
  },

  // BCM/Approver pending list (status=2) based on latest NextApproverUserIdCode
  getServiceRequestListApprover: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.createdBy) queryParams.append("createdBy", params.createdBy);
    if (params.statusCode) queryParams.append("statusCode", params.statusCode);
    if (params.statusCodesCsv) queryParams.append("statusCodesCsv", params.statusCodesCsv);
    if (params.businessVerticalCodesCsv)
      queryParams.append("businessVerticalCodesCsv", params.businessVerticalCodesCsv);
    if (params.valueSlab) queryParams.append("valueSlab", params.valueSlab);
    if (params.raisedByUserIdCode) queryParams.append("raisedByUserIdCode", params.raisedByUserIdCode);
    if (params.queryRaised === true || String(params.queryRaised).toLowerCase() === "true") {
      queryParams.append("queryRaised", "true");
    }
    if (params.queryToRespond === true || String(params.queryToRespond).toLowerCase() === "true") {
      queryParams.append("queryToRespond", "true");
    }
    if (params.specialCase === true || String(params.specialCase).toLowerCase() === "true") {
      queryParams.append("specialCase", "true");
    }
    if (params.slaBreached === true || String(params.slaBreached).toLowerCase() === "true") {
      queryParams.append("slaBreached", "true");
    }
    if (params.fromDate) queryParams.append("fromDate", params.fromDate);
    if (params.toDate) queryParams.append("toDate", params.toDate);
    if (params.searchText) queryParams.append("searchText", params.searchText);
    queryParams.append("pageNumber", params.pageNumber || 1);
    queryParams.append("pageSize", params.pageSize || 50);

    const response = await api.get(`/api/complaints/sr-list-approver?${queryParams}`, {
      signal: params.signal,
      timeout: params.timeout ?? 60000,
    });
    return response.data;
  },

  /**
   * Admin global SR list (`/admin-service-requests`). Same SP as approver list; omits approver user filter
   * unless queryRaised is true (then createdBy / claims identify who raised the queries).
   */
  getServiceRequestListAdmin: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.createdBy) queryParams.append("createdBy", params.createdBy);
    if (params.statusCode) queryParams.append("statusCode", params.statusCode);
    if (params.statusCodesCsv) queryParams.append("statusCodesCsv", params.statusCodesCsv);
    if (params.businessVerticalCodesCsv)
      queryParams.append("businessVerticalCodesCsv", params.businessVerticalCodesCsv);
    if (params.marketCode) queryParams.append("marketCode", params.marketCode);
    if (params.srTypeCode) queryParams.append("srTypeCode", params.srTypeCode);
    if (params.settlementType) queryParams.append("settlementType", params.settlementType);
    if (params.raisedByUserIdCode) queryParams.append("raisedByUserIdCode", params.raisedByUserIdCode);
    if (params.queryRaised === true || String(params.queryRaised).toLowerCase() === "true") {
      queryParams.append("queryRaised", "true");
    }
    if (params.fromDate) queryParams.append("fromDate", params.fromDate);
    if (params.toDate) queryParams.append("toDate", params.toDate);
    if (params.zoneName) queryParams.append("zoneName", params.zoneName);
    if (params.repeatComplaintsScopeFromDate)
      queryParams.append(
        "repeatComplaintsScopeFromDate",
        params.repeatComplaintsScopeFromDate
      );
    if (params.repeatComplaintsScopeToDate)
      queryParams.append(
        "repeatComplaintsScopeToDate",
        params.repeatComplaintsScopeToDate
      );
    if (
      params.repeatComplaintsDrilldown === true ||
      String(params.repeatComplaintsDrilldown).toLowerCase() === "true" ||
      params.repeatComplaintsDrilldown === 1 ||
      String(params.repeatComplaintsDrilldown) === "1"
    ) {
      queryParams.append("repeatComplaintsDrilldown", "true");
    }
    if (params.searchText) queryParams.append("searchText", params.searchText);
    queryParams.append("pageNumber", params.pageNumber || 1);
    queryParams.append("pageSize", params.pageSize || 50);

    const response = await api.get(`/api/complaints/sr-list-admin?${queryParams}`);
    return response.data;
  },

  /**
   * Admin: assign SR line to another user (SRSTATUSHISTORY).
   * Body: { srCode, childSRNumber?, delegateToUserIdCode, comments? }
   */
  delegateServiceRequestAssignment: async (body, changedBy) => {
    const queryParams = new URLSearchParams();
    if (changedBy) queryParams.append("changedBy", changedBy);
    const q = queryParams.toString();
    const url = q
      ? `/api/complaints/by-code/delegate-assignment?${q}`
      : "/api/complaints/by-code/delegate-assignment";
    const response = await api.post(url, body);
    return response.data;
  },

  /** Get SR status list from Greenlam.SRSTATUSMASTER for filter dropdowns */
  getSRStatuses: async () => {
    const response = await api.get("/api/complaints/sr-statuses");
    return response.data;
  },

  /**
   * Get full Service Request detail by SRCode (header, lines, attachments, checklist, history, comments, RCA).
   * Returns { success, data: { header, invoiceDetails, attachments, checklistResponses, statusHistory, commentsHistory, rootCauseAnalysis }, message }.
   */
  getServiceRequestByCode: async (parentSrCode, childSrCode = null, changedBy = null) => {
    const parentTrimmed =
      parentSrCode && String(parentSrCode).trim()
        ? String(parentSrCode).trim()
        : "";
    const childTrimmed =
      childSrCode && String(childSrCode).trim()
        ? String(childSrCode).trim()
        : "";

    if (!parentTrimmed && !childTrimmed) {
      return { success: false, data: null, message: "parentSrCode or childSrCode is required" };
    }

    const params = {};
    if (parentTrimmed) params.parentSrCode = parentTrimmed;
    if (childTrimmed) params.childSrCode = childTrimmed;
    if (changedBy && String(changedBy).trim()) params.changedBy = String(changedBy).trim();

    const response = await api.get("/api/complaints/by-code", { params });
    return response.data;
  },

  /**
   * Get only Service Request header by SRCode.
   * Accepts (srCode) or ({ parentSrCode?, srNumber?, childSrCode?, srCode? }).
   * Returns { success, data: header, message }.
   */
  getServiceRequestHeader: async (srCodeOrParams) => {
    const params = {};
    if (typeof srCodeOrParams === "string" && srCodeOrParams?.trim()) {
      params.srCode = srCodeOrParams.trim();
    } else if (srCodeOrParams && typeof srCodeOrParams === "object") {
      if (srCodeOrParams.srCode?.trim()) params.srCode = srCodeOrParams.srCode.trim();
      if (srCodeOrParams.parentSrCode?.trim()) params.parentSrCode = srCodeOrParams.parentSrCode.trim();
      if (srCodeOrParams.srNumber?.trim()) params.srNumber = srCodeOrParams.srNumber.trim();
      if (srCodeOrParams.childSrCode?.trim()) params.childSrCode = srCodeOrParams.childSrCode.trim();
      if (srCodeOrParams.changedBy?.trim()) params.changedBy = srCodeOrParams.changedBy.trim();
    }
    if (!params.srCode && !params.parentSrCode && !params.childSrCode) {
      return { success: false, data: null, message: "parentSrCode or childSrCode (or srCode) is required" };
    }
    const response = await api.get("/api/complaints/by-code/header", { params });
    return response.data;
  },

  /**
   * Get only Service Request invoice line items by SRCode.
   * Returns { success, data: invoiceDetails[], message }.
   */
  getServiceRequestInvoice: async (srCode, childSrCode = null, changedBy = null) => {
    if (!srCode || !String(srCode).trim()) {
      return { success: false, data: null, message: "srCode is required" };
    }
    const response = await api.get("/api/complaints/by-code/invoice", {
      params: {
        srCode: String(srCode).trim(),
        ...(childSrCode && String(childSrCode).trim() ? { childSrCode: String(childSrCode).trim() } : {}),
        ...(changedBy && String(changedBy).trim() ? { changedBy: String(changedBy).trim() } : {}),
      },
    });
    return response.data;
  },

  /**
   * Get only Service Request attachments by SRCode.
   * Returns { success, data: attachments[], message }.
   */
  getServiceRequestAttachments: async (srCode) => {
    if (!srCode || !String(srCode).trim()) {
      return { success: false, data: null, message: "srCode is required" };
    }
    const response = await api.get("/api/complaints/by-code/attachments", {
      params: { srCode: String(srCode).trim() },
    });
    return response.data;
  },

  /**
   * Get only Service Request checklist responses by SRCode.
   * Returns { success, data: checklistResponses[], message }.
   */
  getServiceRequestChecklist: async (srCode) => {
    if (!srCode || !String(srCode).trim()) {
      return { success: false, data: null, message: "srCode is required" };
    }
    const response = await api.get("/api/complaints/by-code/checklist", {
      params: { srCode: String(srCode).trim() },
    });
    return response.data;
  },

  /**
   * Approve or reject Material Section items for an SR.
   * Body: { srCode, childSrCode?, action: 'Approve'|'Reject', remarks?, factoryIntentNo?, factoryIntentDate?, settlementType?, items: [...] }
   * Query: changedBy (optional, UserIdCode; falls back to auth if not provided)
   */
  approveRejectServiceRequestItems: async (payload, changedBy = null) => {
    // Long-running: SP next-approver lookup can be slow; admin email is fire-and-forget on failure.
    const params = changedBy ? { changedBy: String(changedBy).trim() } : {};
    const response = await api.post("/api/complaints/by-code/approve-reject-items", payload, {
      params,
      __longRunning: true,
      timeout: API_LONG_RUNNING_TIMEOUT_MS,
    });
    return response.data;
  },

  /**
   * Plant QC: save Component Details (Component Qty editable) for one material line.
   */
  saveSrComponentDetails: async (payload) => {
    const response = await api.post(
      "/api/complaints/by-code/save-component-details",
      payload
    );
    return response.data;
  },

  /**
   * Fetch printable SR HTML (exact layout from database/Print template).
   * Returns axios response (text/html). Caller should open in a new window for print/PDF.
   */
  getComplaintPrintPdf: async ({ srCode, childSrCode, scope, onProgress } = {}) => {
    const parent = (srCode ?? "").toString().trim();
    const child = (childSrCode ?? "").toString().trim();
    const printScope = String(scope || "all").toLowerCase() === "checklist" ? "checklist" : "all";
    if (!parent && !child) {
      throw new Error("srCode or childSrCode is required");
    }
    const apiBaseUrl = getApiBaseUrl();
    return api.get("/api/service-request-print", {
      params: {
        ...(parent ? { srCode: parent } : {}),
        ...(child ? { childSrCode: child } : {}),
        ...(apiBaseUrl ? { apiBaseUrl } : {}),
        scope: printScope,
      },
      responseType: "text",
      // Must accept HTML; otherwise ApiController [Produces] negotiation can return 406.
      headers: {
        Accept: "text/html, text/plain, */*",
      },
      timeout: API_LONG_RUNNING_TIMEOUT_MS,
      __longRunning: true,
      ...(typeof onProgress === "function"
        ? {
            onDownloadProgress: (evt) => {
              try {
                const total = Number(evt?.total ?? 0);
                const loaded = Number(evt?.loaded ?? 0);
                const percent =
                  total > 0 ? Math.max(0, Math.min(100, Math.round((loaded / total) * 100))) : null;
                onProgress({ loaded, total, percent });
              } catch {
                // ignore progress callback failures
              }
            },
          }
        : {}),
    });
  },
};

// Master Data API
export const masterDataAPI = {
  getCountries: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.srTypeCode) queryParams.append("srTypeCode", params.srTypeCode);
    if (params.isActive !== undefined) queryParams.append("isActive", params.isActive);
    const response = await api.get(
      `/api/masterdata/countries${queryParams.toString() ? `?${queryParams}` : ""}`
    );
    return response.data;
  },
  getTerritories: async (countryCode) => {
    const response = await api.get(
      `/api/masterdata/territories${
        countryCode ? `?countryCode=${countryCode}` : ""
      }`
    );
    return response.data;
  },
  getPlants: async (countryCode, stateCode) => {
    const params = new URLSearchParams();
    if (countryCode) params.append("countryCode", countryCode);
    if (stateCode) params.append("stateCode", stateCode);
    const response = await api.get(
      `/api/masterdata/plants${params.toString() ? `?${params}` : ""}`
    );
    return response.data;
  },
  getVerticals: async () => {
    const response = await api.get("/api/masterdata/verticals");
    return response.data;
  },
  getProducts: async (
    verticalId,
    plantId,
    searchText,
    pageNumber = 1,
    pageSize = 50
  ) => {
    const params = new URLSearchParams();
    if (verticalId) params.append("verticalId", verticalId);
    if (plantId) params.append("plantId", plantId);
    if (searchText) params.append("searchText", searchText);
    params.append("pageNumber", pageNumber);
    params.append("pageSize", pageSize);
    const response = await api.get(`/api/masterdata/products?${params}`);
    return response.data;
  },
  getComponents: async (productId, verticalId) => {
    const params = new URLSearchParams();
    if (productId) params.append("productId", productId);
    if (verticalId) params.append("verticalId", verticalId);
    const response = await api.get(`/api/masterdata/components?${params}`);
    return response.data;
  },
  getRDC: async (isActive) => {
    const params = new URLSearchParams();
    if (isActive !== undefined) params.append("isActive", isActive);
    const response = await api.get(
      `/api/masterdata/rdc${params.toString() ? `?${params}` : ""}`
    );
    return response.data;
  },
  getBranches: async (territoryId, rdcId, countryId) => {
    const params = new URLSearchParams();
    if (territoryId) params.append("territoryId", territoryId);
    if (rdcId) params.append("rdcId", rdcId);
    if (countryId) params.append("countryId", countryId);
    const response = await api.get(`/api/masterdata/branches?${params}`);
    return response.data;
  },
  getMarkets: async (searchText = "", pageNumber = 1, pageSize = 20) => {
    const params = new URLSearchParams();
    if (searchText) params.append("searchText", searchText);
    params.append("pageNumber", pageNumber);
    params.append("pageSize", pageSize);
    const response = await api.get(`/api/masterdata/markets?${params}`);
    return response.data;
  },

  getComplaintTypes: async (market = null) => {
    const params = new URLSearchParams();
    if (market) params.append("market", market);
    const response = await api.get(`/api/masterdata/complaint-types?${params}`);
    return response.data;
  },
  getSRTypes: async (marketCode = null, isActive = true) => {
    const params = new URLSearchParams();
    if (marketCode) params.append("marketCode", marketCode);
    if (isActive !== undefined) params.append("isActive", isActive);
    const response = await api.get(`/api/masterdata/sr-types?${params}`);
    return response.data;
  },
  getSRTypesByMarketNameForRaiseComplaint: async (marketName = null, isActive = true) => {
    const params = new URLSearchParams();
    if (marketName) params.append("marketName", marketName);
    if (isActive !== undefined) params.append("isActive", isActive);
    const response = await api.get(`/api/masterdata/sr-types-by-market-name-for-raise-complaint?${params}`);
    return response.data;
  },
  // Material Section dropdowns (Raise Complaint - Component Details popup)
  getMaterialGroups: async (isActive = true) => {
    const params = new URLSearchParams();
    if (isActive !== undefined) params.append("isActive", isActive);
    const response = await api.get(`/api/material-section/material-groups?${params}`);
    return response.data;
  },
  getComponentCategories: async (isActive = true, materialGroupCode = null) => {
    const params = new URLSearchParams();
    if (isActive !== undefined) params.append("isActive", isActive);
    if (materialGroupCode) params.append("materialGroupCode", materialGroupCode);
    const response = await api.get(`/api/material-section/component-categories?${params}`);
    return response.data;
  },
  getComponentNames: async (componentCatgCode = null, isActive = true) => {
    const params = new URLSearchParams();
    if (componentCatgCode) params.append("componentCatgCode", componentCatgCode);
    if (isActive !== undefined) params.append("isActive", isActive);
    const response = await api.get(`/api/material-section/component-names?${params}`);
    return response.data;
  },
  getComponentIssues: async (isActive = true) => {
    const params = new URLSearchParams();
    if (isActive !== undefined) params.append("isActive", isActive);
    const response = await api.get(`/api/material-section/component-issues?${params}`);
    return response.data;
  },
  getStates: async (countryCode) => {
    const response = await api.get(
      `/api/masterdata/states${
        countryCode ? `?countryCode=${countryCode}` : ""
      }`
    );
    return response.data;
  },
  getRegions: async (countryCode) => {
    const response = await api.get(
      `/api/masterdata/regions${
        countryCode ? `?countryCode=${countryCode}` : ""
      }`
    );
    return response.data;
  },
  getZones: async (regionCode) => {
    const response = await api.get(
      `/api/masterdata/zones${regionCode ? `?regionCode=${regionCode}` : ""}`
    );
    return response.data;
  },
  getCompanies: async (countryCode, stateCode) => {
    const params = new URLSearchParams();
    if (countryCode) params.append("countryCode", countryCode);
    if (stateCode) params.append("stateCode", stateCode);
    const response = await api.get(
      `/api/masterdata/companies${params.toString() ? `?${params}` : ""}`
    );
    return response.data;
  },
  getUserTypes: async (companyCode) => {
    const response = await api.get(
      `/api/masterdata/user-types${
        companyCode ? `?companyCode=${companyCode}` : ""
      }`
    );
    return response.data;
  },
};

// PPB API
export const ppbAPI = {
  getDropdownValues: async (dropdownType) => {
    const response = await api.get(`/api/ppb/dropdown/${dropdownType}`);
    return response.data;
  },
  getThicknessValues: async () => {
    const response = await api.get(`/api/ppb/dropdown/thickness`);
    return response.data;
  },
  saveChecklist: async (data) => {
    const response = await api.post("/api/ppb/checklist", data);
    return response.data;
  },
  getChecklist: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.srRecID) queryParams.append("srRecID", params.srRecID);
    if (params.srNo) queryParams.append("srNo", params.srNo);
    if (params.complaintId)
      queryParams.append("complaintId", params.complaintId);
    if (params.complaintProductId)
      queryParams.append("complaintProductId", params.complaintProductId);
    const response = await api.get(`/api/ppb/checklist?${queryParams}`);
    return response.data;
  },
};

// Checklist Document API
export const checklistDocumentAPI = {
  getAll: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.searchText) queryParams.append("searchText", params.searchText);
    if (params.status) queryParams.append("status", params.status);
    if (params.catgCode) queryParams.append("catgCode", params.catgCode);
    if (params.productTypeCode) queryParams.append("productTypeCode", params.productTypeCode);
    if (params.productCode) queryParams.append("productCode", params.productCode);
    if (params.bvCode) queryParams.append("bvCode", params.bvCode);
    if (params.pageNumber) queryParams.append("pageNumber", params.pageNumber);
    if (params.pageSize) queryParams.append("pageSize", params.pageSize);
    const response = await api.get(`/api/checklistdocument?${queryParams}`);
    return response.data;
  },
  getById: async (slNo) => {
    const response = await api.get(`/api/checklistdocument/${slNo}`);
    return response.data;
  },
  upload: async (formData) => {
    const response = await api.post("/api/checklistdocument/upload", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      timeout: API_UPLOAD_TIMEOUT_MS,
    });
    return response.data;
  },
  update: async (slNo, data) => {
    const response = await api.put(`/api/checklistdocument/${slNo}`, data);
    return response.data;
  },
  updateStatus: async (slNo, isActive) => {
    const response = await api.patch(`/api/checklistdocument/${slNo}/status`, { isActive });
    return response.data;
  },
  delete: async (slNo) => {
    const response = await api.delete(`/api/checklistdocument/${slNo}`);
    return response.data;
  },
  download: async (slNo) => {
    const response = await api.get(`/api/checklistdocument/${slNo}/download`, {
      responseType: "blob",
    });
    return response.data;
  },
};

// Checklist Template API
export const checklistTemplateAPI = {
  getAll: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.searchText) queryParams.append("searchText", params.searchText);
    if (params.status) queryParams.append("status", params.status);
    if (params.marketCode) queryParams.append("marketCode", params.marketCode);
    if (params.catgCode) queryParams.append("catgCode", params.catgCode);
    if (params.productTypeCode) queryParams.append("productTypeCode", params.productTypeCode);
    if (params.productQualityCode) queryParams.append("productQualityCode", params.productQualityCode);
    if (params.bvCode) queryParams.append("bvCode", params.bvCode);
    if (params.documentSLNo) queryParams.append("documentSLNo", params.documentSLNo);
    if (params.invoiceNo) queryParams.append("invoiceNo", params.invoiceNo);
    if (params.productCode) queryParams.append("productCode", params.productCode);
    if (params.productCategoryId) queryParams.append("productCategoryId", params.productCategoryId);
    if (params.marketName) queryParams.append("marketName", params.marketName);
    if (params.businessVertical)
      queryParams.append("businessVertical", params.businessVertical);
    else if (params.divisionName)
      queryParams.append("divisionName", params.divisionName);
    if (params.pageNumber) queryParams.append("pageNumber", params.pageNumber);
    if (params.pageSize) queryParams.append("pageSize", params.pageSize);
    const response = await api.get(`/api/checklisttemplate?${queryParams}`);
    return response.data;
  },
  getAllForRaisedQuery: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.searchText) queryParams.append("searchText", params.searchText);
    if (params.status) queryParams.append("status", params.status);
    if (params.marketCode) queryParams.append("marketCode", params.marketCode);
    if (params.catgCode) queryParams.append("catgCode", params.catgCode);
    if (params.productTypeCode) queryParams.append("productTypeCode", params.productTypeCode);
    if (params.productQualityCode) queryParams.append("productQualityCode", params.productQualityCode);
    if (params.bvCode) queryParams.append("bvCode", params.bvCode);
    if (params.documentSLNo) queryParams.append("documentSLNo", params.documentSLNo);
    if (params.invoiceNo) queryParams.append("invoiceNo", params.invoiceNo);
    if (params.productCode) queryParams.append("productCode", params.productCode);
    if (params.productCategoryId) queryParams.append("productCategoryId", params.productCategoryId);
    if (params.marketName) queryParams.append("marketName", params.marketName);
    if (params.businessVertical)
      queryParams.append("businessVertical", params.businessVertical);
    else if (params.divisionName)
      queryParams.append("divisionName", params.divisionName);
    if (params.pageNumber) queryParams.append("pageNumber", params.pageNumber);
    if (params.pageSize) queryParams.append("pageSize", params.pageSize);
    const response = await api.get(`/api/checklisttemplate/raised-query?${queryParams}`);
    return response.data;
  },
  getByCode: async (templateCode) => {
    const response = await api.get(`/api/checklisttemplate/${templateCode}`);
    return response.data;
  },
  getByDocument: async (documentSLNo) => {
    const response = await api.get(`/api/checklisttemplate/by-document/${documentSLNo}`);
    return response.data;
  },
  getNextCode: async () => {
    const response = await api.get("/api/checklisttemplate/next-code");
    return response.data;
  },
  create: async (data, createdBy) => {
    const queryParams = new URLSearchParams();
    if (createdBy) queryParams.append("createdBy", createdBy);
    const response = await api.post(`/api/checklisttemplate${queryParams.toString() ? `?${queryParams}` : ""}`, data);
    return response.data;
  },
  update: async (templateCode, data, modifiedBy) => {
    const queryParams = new URLSearchParams();
    if (modifiedBy) queryParams.append("modifiedBy", modifiedBy);
    const response = await api.put(`/api/checklisttemplate/${templateCode}${queryParams.toString() ? `?${queryParams}` : ""}`, data);
    return response.data;
  },
  updateStatus: async (templateCode, isActive, modifiedBy) => {
    const queryParams = new URLSearchParams();
    if (modifiedBy) queryParams.append("modifiedBy", modifiedBy);
    const response = await api.patch(`/api/checklisttemplate/${templateCode}/status${queryParams.toString() ? `?${queryParams}` : ""}`, { isActive });
    return response.data;
  },
  delete: async (templateCode, modifiedBy) => {
    const queryParams = new URLSearchParams();
    if (modifiedBy) queryParams.append("modifiedBy", modifiedBy);
    const response = await api.delete(`/api/checklisttemplate/${templateCode}${queryParams.toString() ? `?${queryParams}` : ""}`);
    return response.data;
  },
  copy: async (templateCode, createdBy) => {
    const queryParams = new URLSearchParams();
    if (createdBy) queryParams.append("createdBy", createdBy);
    const encodedCode = encodeURIComponent(templateCode);
    const response = await api.post(
      `/api/checklisttemplate/${encodedCode}/copy${queryParams.toString() ? `?${queryParams}` : ""}`
    );
    return response.data;
  },
};

// Checklist Question API
export const checklistQuestionAPI = {
  
  getByTemplate: async (templateCode, params = {}) => {
    debugger;
    const queryParams = new URLSearchParams();
    if (params.parentQuestionCode) queryParams.append("parentQuestionCode", params.parentQuestionCode);
    if (params.pageNumber) queryParams.append("pageNumber", params.pageNumber);
    if (params.pageSize) queryParams.append("pageSize", params.pageSize);
    const response = await api.get(`/api/checklistquestion/template/${templateCode}?${queryParams}`);
    return response.data;
  },
  getByCode: async (questionCode) => {
    debugger;
    const response = await api.get(`/api/checklistquestion/${questionCode}`);
    return response.data;
  },
  create: async (data) => {
    debugger;
    const response = await api.post("/api/checklistquestion", data);
    return response.data;
  },
  update: async (questionCode, data) => {
    debugger;
    const response = await api.put(`/api/checklistquestion/${questionCode}`, data);
    return response.data;
  },
  delete: async (questionCode) => {
    debugger;
    const response = await api.delete(`/api/checklistquestion/${questionCode}`);
    return response.data;
  },
  reorder: async (templateCode, questionCodes) => {
    debugger;
    const response = await api.post("/api/checklistquestion/reorder", {
      templateCode,
      questionCodes,
    });
    return response.data;
  },
  getQuestionTypes: async () => {
    debugger;
    const response = await api.get("/api/checklistquestion/question-types");
    return response.data;
  },
  getQuestionsHierarchy: async (templateCode) => {
    const response = await api.get(`/api/checklistquestion/template/${templateCode}/hierarchy`);
    return response.data;
    debugger;
  },
  getQuestionsByParentAnswer: async (templateCode, parentQuestionCode, parentAnswerValue) => {
    const response = await api.get(
      `/api/checklistquestion/template/${templateCode}/parent/${parentQuestionCode}/answer/${parentAnswerValue}`
    );
    return response.data;
  },
  createQuestionTree: async (data) => {
    debugger;
    const response = await api.post("/api/checklistquestion/tree", data);
    return response.data;
  },
  upsertQuestionTree: async (data) => {
    const response = await api.post("/api/checklistquestion/tree-upsert", data);
    return response.data;
  },
  saveQuestion: async (data, templateCode = null, parentQuestionCode = null) => {
    debugger;
    try {
      const queryParams = new URLSearchParams();
      if (templateCode) queryParams.append("templateCode", templateCode);
      if (parentQuestionCode) queryParams.append("parentQuestionCode", parentQuestionCode);
      const url = queryParams.toString()
        ? `/api/checklistquestion/save-question?${queryParams}`
        : "/api/checklistquestion/save-question";
      
      console.log("Saving question to:", url);
      console.log("Question data:", JSON.stringify(data, null, 2));
      
      const response = await api.post(url, data);
      console.log("Save response:", response.data);
      return response.data;
    } catch (error) {
      console.error("Error saving question:", error);
      console.error("Error response:", error.response?.data);
      throw error;
    }
  },
  // Alias methods for consistency
  getQuestions: async (templateCode, params = {}) => {
    debugger;
    return checklistQuestionAPI.getByTemplate(templateCode, params);
  },
  getQuestion: async (questionCode) => {
    debugger;
    return checklistQuestionAPI.getByCode(questionCode);
  },
  createQuestion: async (data) => {
    debugger;
    return checklistQuestionAPI.create(data);
  },
  updateQuestion: async (questionCode, data) => {
    debugger;
    return checklistQuestionAPI.update(questionCode, data);
  },
  deleteQuestion: async (questionCode) => {
    debugger;
    return checklistQuestionAPI.delete(questionCode);
  },
};

// Menu API
export const menuAPI = {
  /** Uses shared `api` instance timeout (1 hour). Optional `requestConfig` can override per call. */
  getUserMenus: async (userIdCode, requestConfig = {}) => {
    const safe = encodeURIComponent(String(userIdCode ?? "").trim());
    const response = await api.get(`/api/menu/user/${safe}`, requestConfig);
    return response.data;
  },
  getAllMenus: async () => {
    const response = await api.get("/api/menu/all");
    return response.data;
  },
  /** /privileges and /privileges/add: items use adminMenuName (MenuMaster.AdminMenuName only). */
  getAllMenusForPrivilegePages: async () => {
    const response = await api.get("/api/menu/all-privilege-pages");
    return response.data;
  },
};

// User API
export const userAPI = {
  getAll: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.search) queryParams.append("search", params.search);
    if (params.status) queryParams.append("status", params.status);
    if (params.marketCode) queryParams.append("marketCode", params.marketCode);
    if (params.bvCode) queryParams.append("bvCode", params.bvCode);
    if (params.userTypeCode) queryParams.append("userTypeCode", params.userTypeCode);
    if (params.countryCode) queryParams.append("countryCode", params.countryCode);
    if (params.zoneCode) queryParams.append("zoneCode", params.zoneCode);
    if (params.regionCode) queryParams.append("regionCode", params.regionCode);
    if (params.territoryCode) queryParams.append("territoryCode", params.territoryCode);
    const response = await api.get(`/api/users?${queryParams}`);
    return response.data;
  },
  getSeByTerritory: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.approverUserIdCode)
      queryParams.append("approverUserIdCode", params.approverUserIdCode);
    if (params.status) queryParams.append("status", params.status);
    const response = await api.get(`/api/users/se-by-territory?${queryParams}`);
    return response.data;
  },
  /** SE Approve modal (Plant spec): user types that have Plant-scoped users. */
  getSeApproveRoutingPlantUserTypeCodes: async () => {
    const response = await api.get("/api/users/se-approve-routing/plant-user-type-codes");
    return response.data;
  },
  /** SE Approve modal: plant codes assigned to Plant users for the selected role. */
  getSeApproveRoutingPlantCodesForRole: async (userTypeCode) => {
    const q = new URLSearchParams();
    if (userTypeCode) q.append("userTypeCode", userTypeCode);
    const response = await api.get(`/api/users/se-approve-routing/plant-codes?${q}`);
    return response.data;
  },
  /** SE Approve modal (Plant): plant-scoped users for role; optional allowedPlantCodes CSV from SR lines. */
  getSeApproveRoutingPlantUsers: async (params = {}) => {
    const q = new URLSearchParams();
    if (params.userTypeCode) q.append("userTypeCode", params.userTypeCode);
    if (params.allowedPlantCodes) q.append("allowedPlantCodes", params.allowedPlantCodes);
    if (params.marketCode) q.append("marketCode", params.marketCode);
    if (params.parentSRCode) q.append("parentSRCode", params.parentSRCode);
    if (params.childSRCode) q.append("childSRCode", params.childSRCode);
    const response = await api.get(`/api/users/se-approve-routing/plant-users?${q}`);
    return response.data;
  },
  getSeApproveRoutingSpecUserTypeCodes: async (userSpecificationType) => {
    const q = new URLSearchParams();
    if (userSpecificationType) q.append("userSpecificationType", userSpecificationType);
    const response = await api.get(`/api/users/se-approve-routing/spec-user-type-codes?${q}`);
    return response.data;
  },
  getSeApproveRoutingSpecUsers: async (params = {}) => {
    const q = new URLSearchParams();
    if (params.userSpecificationType) q.append("userSpecificationType", params.userSpecificationType);
    if (params.userTypeCode) q.append("userTypeCode", params.userTypeCode);
    if (params.allowedScopeCodes) q.append("allowedScopeCodes", params.allowedScopeCodes);
    if (params.marketCode) q.append("marketCode", params.marketCode);
    if (params.parentSRCode) q.append("parentSRCode", params.parentSRCode);
    if (params.childSRCode) q.append("childSRCode", params.childSRCode);
    const response = await api.get(`/api/users/se-approve-routing/spec-users?${q}`);
    return response.data;
  },
  getPaged: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.searchText) queryParams.append("searchText", params.searchText);
    if (params.status) queryParams.append("status", params.status);
    if (params.userTypeCode)
      queryParams.append("userTypeCode", params.userTypeCode);
    if (params.countryCode)
      queryParams.append("countryCode", params.countryCode);
    if (params.zoneCode) queryParams.append("zoneCode", params.zoneCode);
    if (params.regionCode) queryParams.append("regionCode", params.regionCode);
    if (params.bvCode) queryParams.append("bvCode", params.bvCode);
    queryParams.append("pageNumber", params.pageNumber || 1);
    queryParams.append("pageSize", params.pageSize || 50);
    const response = await api.get(`/api/users/paged?${queryParams}`);
    return response.data;
  },
  getNextCode: async () => {
    const response = await api.get(`/api/users/next-code`);
    return response.data;
  },
  getById: async (userIdCode) => {
    const response = await api.get(`/api/users/${userIdCode}`);
    return response.data;
  },
  /** Global helper API — Country / Zone / Region / Territory mapping for one user. */
  getGeography: async (userIdCode) => {
    const response = await api.get(`/api/users/${userIdCode}/geography`);
    return response.data;
  },
  create: async (data, createdBy = "SYSADMIN001") => {
    const response = await api.post(`/api/users?createdBy=${createdBy}`, data);
    return response.data;
  },
  update: async (userIdCode, data, modifiedBy = "SYSADMIN001") => {
    const response = await api.put(
      `/api/users/${userIdCode}?modifiedBy=${modifiedBy}`,
      data
    );
    return response.data;
  },
  updateStatus: async (userIdCode, isActive, modifiedBy = "SYSADMIN001") => {
    const response = await api.patch(
      `/api/users/${userIdCode}/status?modifiedBy=${modifiedBy}`,
      { isActive }
    );
    return response.data;
  },
  delete: async (userIdCode, modifiedBy = "SYSADMIN001") => {
    const response = await api.delete(
      `/api/users/${userIdCode}?modifiedBy=${modifiedBy}`
    );
    return response.data;
  },
  bulkUpload: async (file, createdBy = "SYSADMIN001") => {
    const formData = new FormData();
    formData.append("file", file);
    const response = await api.post(`/api/users/bulk-upload?createdBy=${encodeURIComponent(createdBy)}`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: API_UPLOAD_TIMEOUT_MS,
    });
    return response.data;
  },
  bulkSubmit: async (rows, createdBy = "SYSADMIN001") => {
    const response = await api.post(
      `/api/users/bulk-upload/submit?createdBy=${encodeURIComponent(createdBy)}`,
      { rows }
    );
    return response.data;
  },
  /** Bulk upload sample/template — returns Excel .xlsx blob. */
  downloadBulkTemplate: async () => {
    const response = await api.get(`/api/users/bulk-upload/template`, {
      responseType: "blob",
      timeout: API_UPLOAD_TIMEOUT_MS,
    });
    return response.data;
  },
};

// Product Master API (new schema)
export const productMasterAPI = {
  getPaged: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.searchText) queryParams.append("searchText", params.searchText);
    if (params.status) queryParams.append("status", params.status);
    queryParams.append("pageNumber", params.pageNumber || 1);
    queryParams.append("pageSize", params.pageSize || 50);
    const response = await api.get(`/api/product-master?${queryParams}`);
    return response.data;
  },
  getByCode: async (productCode) => {
    const response = await api.get(`/api/product-master/${productCode}`);
    return response.data;
  },
  getCategories: async (isActive, marketCode) => {
    const params = new URLSearchParams();
    if (isActive !== undefined) params.append("isActive", isActive);
    if (marketCode) params.append("marketCode", marketCode);
    const response = await api.get(
      `/api/product-master/categories${params.toString() ? `?${params}` : ""}`
    );
    return response.data;
  },
  getProductQualities: async (catgCode, productTypeCode, isActive) => {
    const params = new URLSearchParams();
    if (catgCode) params.append("catgCode", catgCode);
    if (productTypeCode) params.append("productTypeCode", productTypeCode);
    if (isActive !== undefined && isActive !== null) params.append("isActive", isActive);
    const response = await api.get(`/api/product-master/product-qualities?${params}`);
    return response.data;
  },
  getProductTypes: async (catgCode, isActive) => {
    const params = new URLSearchParams();
    if (catgCode) params.append("catgCode", catgCode);
    if (isActive !== undefined) params.append("isActive", isActive);
    const response = await api.get(
      `/api/product-master/product-types${
        params.toString() ? `?${params}` : ""
      }`
    );
    return response.data;
  },
  create: async (data, createdBy = "SYSADMIN001") => {
    const response = await api.post(
      `/api/product-master?createdBy=${createdBy}`,
      data
    );
    return response.data;
  },
  update: async (productCode, data, modifiedBy = "SYSADMIN001") => {
    const response = await api.put(
      `/api/product-master/${productCode}?modifiedBy=${modifiedBy}`,
      data
    );
    return response.data;
  },
  updateStatus: async (productCode, isActive, modifiedBy = "SYSADMIN001") => {
    const response = await api.patch(
      `/api/product-master/${productCode}/status?modifiedBy=${modifiedBy}`,
      { isActive }
    );
    return response.data;
  },
  delete: async (productCode, modifiedBy = "SYSADMIN001") => {
    const response = await api.delete(
      `/api/product-master/${productCode}?modifiedBy=${modifiedBy}`
    );
    return response.data;
  },
  getNextCode: async () => {
    const response = await api.get(`/api/product-master/next-code`);
    return response.data;
  },
};

// Plant Master API
export const plantMasterAPI = {
  getPaged: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.searchText) queryParams.append("searchText", params.searchText);
    if (params.status) queryParams.append("status", params.status);
    if (params.countryCode)
      queryParams.append("countryCode", params.countryCode);
    if (params.stateCode) queryParams.append("stateCode", params.stateCode);
    queryParams.append("pageNumber", params.pageNumber || 1);
    queryParams.append("pageSize", params.pageSize || 50);
    const response = await api.get(`/api/plant-master?${queryParams}`);
    return response.data;
  },
  getByCode: async (plantCode) => {
    const response = await api.get(`/api/plant-master/${plantCode}`);
    return response.data;
  },
  create: async (data, createdBy = "SYSADMIN001") => {
    const response = await api.post(
      `/api/plant-master?createdBy=${createdBy}`,
      data
    );
    return response.data;
  },
  update: async (plantCode, data, modifiedBy = "SYSADMIN001") => {
    const response = await api.put(
      `/api/plant-master/${plantCode}?modifiedBy=${modifiedBy}`,
      data
    );
    return response.data;
  },
  updateStatus: async (plantCode, isActive, modifiedBy = "SYSADMIN001") => {
    const response = await api.patch(
      `/api/plant-master/${plantCode}/status?modifiedBy=${modifiedBy}`,
      { isActive }
    );
    return response.data;
  },
  delete: async (plantCode, modifiedBy = "SYSADMIN001") => {
    const response = await api.delete(
      `/api/plant-master/${plantCode}?modifiedBy=${modifiedBy}`
    );
    return response.data;
  },
  getNextCode: async () => {
    const response = await api.get(`/api/plant-master/next-code`);
    return response.data;
  },
};

// Region Master API
export const regionMasterAPI = {
  getPaged: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.searchText) queryParams.append("searchText", params.searchText);
    if (params.status) queryParams.append("status", params.status);
    if (params.countryCode)
      queryParams.append("countryCode", params.countryCode);
    if (params.zoneCode) queryParams.append("zoneCode", params.zoneCode);
    queryParams.append("pageNumber", params.pageNumber || 1);
    queryParams.append("pageSize", params.pageSize || 50);
    const response = await api.get(`/api/region-master?${queryParams}`);
    return response.data;
  },
  getByCode: async (regionCode) => {
    const response = await api.get(`/api/region-master/${regionCode}`);
    return response.data;
  },
  create: async (data, createdBy = "SYSADMIN001") => {
    const response = await api.post(
      `/api/region-master?createdBy=${createdBy}`,
      data
    );
    return response.data;
  },
  update: async (regionCode, data, modifiedBy = "SYSADMIN001") => {
    const response = await api.put(
      `/api/region-master/${regionCode}?modifiedBy=${modifiedBy}`,
      data
    );
    return response.data;
  },
  updateStatus: async (regionCode, isActive, modifiedBy = "SYSADMIN001") => {
    const response = await api.patch(
      `/api/region-master/${regionCode}/status?modifiedBy=${modifiedBy}`,
      { isActive }
    );
    return response.data;
  },
  delete: async (regionCode, modifiedBy = "SYSADMIN001") => {
    const response = await api.delete(
      `/api/region-master/${regionCode}?modifiedBy=${modifiedBy}`
    );
    return response.data;
  },
  getNextCode: async () => {
    const response = await api.get(`/api/region-master/next-code`);
    return response.data;
  },
};

// Territory Master API
export const territoryMasterAPI = {
  getPaged: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.searchText) queryParams.append("searchText", params.searchText);
    if (params.status) queryParams.append("status", params.status);
    if (params.countryCode)
      queryParams.append("countryCode", params.countryCode);
    if (params.regionCode) queryParams.append("regionCode", params.regionCode);
    if (params.zoneCode) queryParams.append("zoneCode", params.zoneCode);
    queryParams.append("pageNumber", params.pageNumber || 1);
    queryParams.append("pageSize", params.pageSize || 50);
    const response = await api.get(`/api/territory-master?${queryParams}`);
    return response.data;
  },
  getByCode: async (territoryCode) => {
    const response = await api.get(`/api/territory-master/${territoryCode}`);
    return response.data;
  },
  create: async (data, createdBy = "SYSADMIN001") => {
    const response = await api.post(
      `/api/territory-master?createdBy=${createdBy}`,
      data
    );
    return response.data;
  },
  update: async (territoryCode, data, modifiedBy = "SYSADMIN001") => {
    const response = await api.put(
      `/api/territory-master/${territoryCode}?modifiedBy=${modifiedBy}`,
      data
    );
    return response.data;
  },
  updateStatus: async (territoryCode, isActive, modifiedBy = "SYSADMIN001") => {
    const response = await api.patch(
      `/api/territory-master/${territoryCode}/status?modifiedBy=${modifiedBy}`,
      { isActive }
    );
    return response.data;
  },
  delete: async (territoryCode, modifiedBy = "SYSADMIN001") => {
    const response = await api.delete(
      `/api/territory-master/${territoryCode}?modifiedBy=${modifiedBy}`
    );
    return response.data;
  },
  getNextCode: async () => {
    const response = await api.get(`/api/territory-master/next-code`);
    return response.data;
  },
  getZones: async (countryCode) => {
    const params = new URLSearchParams();
    if (countryCode) params.append("countryCode", countryCode);
    params.append("isActive", "true");
    const response = await api.get(
      `/api/territory-master/zones${params.toString() ? `?${params}` : ""}`
    );
    return response.data;
  },
  getRegions: async (countryCode, zoneCode) => {
    const params = new URLSearchParams();
    if (countryCode) params.append("countryCode", countryCode);
    if (zoneCode) params.append("zoneCode", zoneCode);
    params.append("isActive", "true");
    const response = await api.get(
      `/api/territory-master/regions${params.toString() ? `?${params}` : ""}`
    );
    return response.data;
  },
};

// RDC Master API
export const rdcMasterAPI = {
  getPaged: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.searchText) queryParams.append("searchText", params.searchText);
    if (params.status) queryParams.append("status", params.status);
    if (params.regionCode) queryParams.append("regionCode", params.regionCode);
    queryParams.append("pageNumber", params.pageNumber || 1);
    queryParams.append("pageSize", params.pageSize || 50);
    const response = await api.get(`/api/rdc-master?${queryParams}`);
    return response.data;
  },
  getByCode: async (rdcCode) => {
    const response = await api.get(`/api/rdc-master/${rdcCode}`);
    return response.data;
  },
  create: async (data, createdBy = "SYSADMIN001") => {
    const response = await api.post(
      `/api/rdc-master?createdBy=${createdBy}`,
      data
    );
    return response.data;
  },
  update: async (rdcCode, data, modifiedBy = "SYSADMIN001") => {
    const response = await api.put(
      `/api/rdc-master/${rdcCode}?modifiedBy=${modifiedBy}`,
      data
    );
    return response.data;
  },
  updateStatus: async (rdcCode, isActive, modifiedBy = "SYSADMIN001") => {
    const response = await api.patch(
      `/api/rdc-master/${rdcCode}/status?modifiedBy=${modifiedBy}`,
      { isActive }
    );
    return response.data;
  },
  delete: async (rdcCode, modifiedBy = "SYSADMIN001") => {
    const response = await api.delete(
      `/api/rdc-master/${rdcCode}?modifiedBy=${modifiedBy}`
    );
    return response.data;
  },
  getNextCode: async () => {
    const response = await api.get(`/api/rdc-master/next-code`);
    return response.data;
  },
};

// Zone Master API
export const zoneMasterAPI = {
  getPaged: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.searchText) queryParams.append("searchText", params.searchText);
    if (params.status) queryParams.append("status", params.status);
    if (params.countryCode)
      queryParams.append("countryCode", params.countryCode);
    queryParams.append("pageNumber", params.pageNumber || 1);
    queryParams.append("pageSize", params.pageSize || 50);
    const response = await api.get(`/api/zone-master?${queryParams}`);
    return response.data;
  },
  getByCode: async (zoneCode) => {
    const response = await api.get(`/api/zone-master/${zoneCode}`);
    return response.data;
  },
  create: async (data, createdBy = "SYSADMIN001") => {
    const response = await api.post(
      `/api/zone-master?createdBy=${createdBy}`,
      data
    );
    return response.data;
  },
  update: async (zoneCode, data, modifiedBy = "SYSADMIN001") => {
    const response = await api.put(
      `/api/zone-master/${zoneCode}?modifiedBy=${modifiedBy}`,
      data
    );
    return response.data;
  },
  updateStatus: async (zoneCode, isActive, modifiedBy = "SYSADMIN001") => {
    const response = await api.patch(
      `/api/zone-master/${zoneCode}/status?modifiedBy=${modifiedBy}`,
      { isActive }
    );
    return response.data;
  },
  delete: async (zoneCode, modifiedBy = "SYSADMIN001") => {
    const response = await api.delete(
      `/api/zone-master/${zoneCode}?modifiedBy=${modifiedBy}`
    );
    return response.data;
  },
  getNextCode: async () => {
    const response = await api.get(`/api/zone-master/next-code`);
    return response.data;
  },
};

// State Master API
export const stateMasterAPI = {
  getPaged: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.searchText) queryParams.append("searchText", params.searchText);
    if (params.status) queryParams.append("status", params.status);
    if (params.countryCode)
      queryParams.append("countryCode", params.countryCode);
    queryParams.append("pageNumber", params.pageNumber || 1);
    queryParams.append("pageSize", params.pageSize || 50);
    const response = await api.get(`/api/state-master?${queryParams}`);
    return response.data;
  },
  getByCode: async (stateCode) => {
    const response = await api.get(`/api/state-master/${stateCode}`);
    return response.data;
  },
  create: async (data, createdBy = "SYSADMIN001") => {
    const response = await api.post(
      `/api/state-master?createdBy=${createdBy}`,
      data
    );
    return response.data;
  },
  update: async (stateCode, data, modifiedBy = "SYSADMIN001") => {
    const response = await api.put(
      `/api/state-master/${stateCode}?modifiedBy=${modifiedBy}`,
      data
    );
    return response.data;
  },
  updateStatus: async (stateCode, isActive, modifiedBy = "SYSADMIN001") => {
    const response = await api.patch(
      `/api/state-master/${stateCode}/status?modifiedBy=${modifiedBy}`,
      { isActive }
    );
    return response.data;
  },
  delete: async (stateCode, modifiedBy = "SYSADMIN001") => {
    const response = await api.delete(
      `/api/state-master/${stateCode}?modifiedBy=${modifiedBy}`
    );
    return response.data;
  },
  getNextCode: async () => {
    const response = await api.get(`/api/state-master/next-code`);
    return response.data;
  },
};

// Country Master API
export const countryMasterAPI = {
  getPaged: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.searchText) queryParams.append("searchText", params.searchText);
    if (params.status) queryParams.append("status", params.status);
    queryParams.append("pageNumber", params.pageNumber || 1);
    queryParams.append("pageSize", params.pageSize || 50);
    const response = await api.get(`/api/country-master?${queryParams}`);
    return response.data;
  },
  getByCode: async (countryCode) => {
    const response = await api.get(`/api/country-master/${countryCode}`);
    return response.data;
  },
  create: async (data, createdBy = "SYSADMIN001") => {
    const response = await api.post(
      `/api/country-master?createdBy=${createdBy}`,
      data
    );
    return response.data;
  },
  update: async (countryCode, data, modifiedBy = "SYSADMIN001") => {
    const response = await api.put(
      `/api/country-master/${countryCode}?modifiedBy=${modifiedBy}`,
      data
    );
    return response.data;
  },
  updateStatus: async (countryCode, isActive, modifiedBy = "SYSADMIN001") => {
    const response = await api.patch(
      `/api/country-master/${countryCode}/status?modifiedBy=${modifiedBy}`,
      { isActive }
    );
    return response.data;
  },
  delete: async (countryCode, modifiedBy = "SYSADMIN001") => {
    const response = await api.delete(
      `/api/country-master/${countryCode}?modifiedBy=${modifiedBy}`
    );
    return response.data;
  },
  getNextCode: async () => {
    const response = await api.get(`/api/country-master/next-code`);
    return response.data;
  },
};

// Product Vertical Master API
export const productVerticalMasterAPI = {
  getPaged: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.searchText) queryParams.append("searchText", params.searchText);
    if (params.status) queryParams.append("status", params.status);
    if (params.marketCode) queryParams.append("marketCode", params.marketCode);
    queryParams.append("pageNumber", params.pageNumber || 1);
    queryParams.append("pageSize", params.pageSize || 50);
    const response = await api.get(
      `/api/product-vertical-master?${queryParams}`
    );
    return response.data;
  },
  getByCode: async (bvCode) => {
    const response = await api.get(`/api/product-vertical-master/${bvCode}`);
    return response.data;
  },
  create: async (data, createdBy = "SYSADMIN001") => {
    const response = await api.post(
      `/api/product-vertical-master?createdBy=${createdBy}`,
      data
    );
    return response.data;
  },
  update: async (bvCode, data, modifiedBy = "SYSADMIN001") => {
    const response = await api.put(
      `/api/product-vertical-master/${bvCode}?modifiedBy=${modifiedBy}`,
      data
    );
    return response.data;
  },
  updateStatus: async (bvCode, isActive, modifiedBy = "SYSADMIN001") => {
    const response = await api.patch(
      `/api/product-vertical-master/${bvCode}/status?modifiedBy=${modifiedBy}`,
      { isActive }
    );
    return response.data;
  },
  delete: async (bvCode, modifiedBy = "SYSADMIN001") => {
    const response = await api.delete(
      `/api/product-vertical-master/${bvCode}?modifiedBy=${modifiedBy}`
    );
    return response.data;
  },
  getNextCode: async () => {
    const response = await api.get(`/api/product-vertical-master/next-code`);
    return response.data;
  },
};

// Component Master API
export const componentMasterAPI = {
  getPaged: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.searchText) queryParams.append("searchText", params.searchText);
    if (params.status) queryParams.append("status", params.status);
    queryParams.append("pageNumber", params.pageNumber || 1);
    queryParams.append("pageSize", params.pageSize || 50);
    const response = await api.get(
      `/api/component-master?${queryParams}`
    );
    return response.data;
  },
  getByCode: async (componentCode) => {
    const response = await api.get(`/api/component-master/${componentCode}`);
    return response.data;
  },
  create: async (data, createdBy = "SYSADMIN001") => {
    const response = await api.post(
      `/api/component-master?createdBy=${createdBy}`,
      data
    );
    return response.data;
  },
  update: async (componentCode, data, modifiedBy = "SYSADMIN001") => {
    const response = await api.put(
      `/api/component-master/${componentCode}?modifiedBy=${modifiedBy}`,
      data
    );
    return response.data;
  },
  updateStatus: async (componentCode, isActive, modifiedBy = "SYSADMIN001") => {
    const response = await api.patch(
      `/api/component-master/${componentCode}/status?modifiedBy=${modifiedBy}`,
      { isActive }
    );
    return response.data;
  },
  delete: async (componentCode, modifiedBy = "SYSADMIN001") => {
    const response = await api.delete(
      `/api/component-master/${componentCode}?modifiedBy=${modifiedBy}`
    );
    return response.data;
  },
  getNextCode: async () => {
    const response = await api.get(`/api/component-master/next-code`);
    return response.data;
  },
};

// Component Master API (old, keep for backward compatibility)
export const productTypeMasterAPI = {
  getAll: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.isActive !== undefined)
      queryParams.append("isActive", params.isActive);
    if (params.catgCode) queryParams.append("catgCode", params.catgCode);
    if (params.countryCode) queryParams.append("countryCode", params.countryCode);
    if (params.srTypeCode) queryParams.append("srTypeCode", params.srTypeCode);
    const response = await api.get(
      `/api/product-master/product-types/all${queryParams.toString() ? `?${queryParams}` : ""}`
    );
    return response.data;
  },
  getPaged: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.searchText) queryParams.append("searchText", params.searchText);
    if (params.status)
      queryParams.append(
        "isActive",
        params.status === "active"
          ? true
          : params.status === "inactive"
          ? false
          : undefined
      );
    if (params.catgCode) queryParams.append("catgCode", params.catgCode);
    if (params.countryCode) queryParams.append("countryCode", params.countryCode);
    if (params.srTypeCode) queryParams.append("srTypeCode", params.srTypeCode);
    queryParams.append("pageNumber", params.pageNumber || 1);
    queryParams.append("pageSize", params.pageSize || 50);
    const response = await api.get(
      `/api/product-master/product-types/paged?${queryParams}`
    );
    return response.data;
  },
  getByCode: async (productTypeCode) => {
    const response = await api.get(
      `/api/product-master/product-types/${productTypeCode}`
    );
    return response.data;
  },
  create: async (data, createdBy = "SYSADMIN001") => {
    const response = await api.post(
      `/api/product-master/product-types?createdBy=${createdBy}`,
      data
    );
    return response.data;
  },
  update: async (productTypeCode, data, modifiedBy = "SYSADMIN001") => {
    const response = await api.put(
      `/api/product-master/product-types/${productTypeCode}?modifiedBy=${modifiedBy}`,
      data
    );
    return response.data;
  },
  updateStatus: async (
    productTypeCode,
    isActive,
    modifiedBy = "SYSADMIN001"
  ) => {
    const response = await api.patch(
      `/api/product-master/product-types/${productTypeCode}/status?modifiedBy=${modifiedBy}`,
      { isActive }
    );
    return response.data;
  },
  delete: async (productTypeCode, modifiedBy = "SYSADMIN001") => {
    const response = await api.delete(
      `/api/product-master/product-types/${productTypeCode}?modifiedBy=${modifiedBy}`
    );
    return response.data;
  },
  getNextCode: async () => {
    const response = await api.get(
      `/api/product-master/product-types/next-code`
    );
    return response.data;
  },
};

// Category Master API
export const categoryMasterAPI = {
  getAll: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.isActive !== undefined)
      queryParams.append("isActive", params.isActive);
    if (params.marketCode) queryParams.append("marketCode", params.marketCode);
    if (params.countryCode) queryParams.append("countryCode", params.countryCode);
    if (params.srTypeCode) queryParams.append("srTypeCode", params.srTypeCode);
    const response = await api.get(
      `/api/category-master/all${queryParams.toString() ? `?${queryParams}` : ""}`
    );
    return response.data;
  },
  getPaged: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.searchText) queryParams.append("searchText", params.searchText);
    if (params.isActive !== undefined)
      queryParams.append("isActive", params.isActive);
    if (params.marketCode) queryParams.append("marketCode", params.marketCode);
    if (params.countryCode) queryParams.append("countryCode", params.countryCode);
    if (params.srTypeCode) queryParams.append("srTypeCode", params.srTypeCode);
    queryParams.append("pageNumber", params.pageNumber || 1);
    queryParams.append("pageSize", params.pageSize || 50);
    const response = await api.get(
      `/api/category-master?${queryParams}`
    );
    return response.data;
  },
  getByCode: async (catgCode) => {
    const response = await api.get(
      `/api/category-master/${catgCode}`
    );
    return response.data;
  },
  create: async (data, createdBy = "SYSADMIN001") => {
    const response = await api.post(
      `/api/category-master?createdBy=${createdBy}`,
      data
    );
    return response.data;
  },
  update: async (catgCode, data, modifiedBy = "SYSADMIN001") => {
    const response = await api.put(
      `/api/category-master/${catgCode}?modifiedBy=${modifiedBy}`,
      data
    );
    return response.data;
  },
  updateStatus: async (
    catgCode,
    isActive,
    modifiedBy = "SYSADMIN001"
  ) => {
    const response = await api.patch(
      `/api/category-master/${catgCode}/status?modifiedBy=${modifiedBy}`,
      { isActive }
    );
    return response.data;
  },
  delete: async (catgCode, modifiedBy = "SYSADMIN001") => {
    const response = await api.delete(
      `/api/category-master/${catgCode}?modifiedBy=${modifiedBy}`
    );
    return response.data;
  },
  getNextCode: async () => {
    const response = await api.get(
      `/api/category-master/next-code`
    );
    return response.data;
  },
};

// Complaint Category Master API
export const complaintCategoryMasterAPI = {
  getPaged: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.searchText) queryParams.append("searchText", params.searchText);
    if (params.status) queryParams.append("status", params.status);
    queryParams.append("pageNumber", params.pageNumber || 1);
    queryParams.append("pageSize", params.pageSize || 50);
    const response = await api.get(`/api/component-category-master?${queryParams}`);
    return response.data;
  },
  getByCode: async (compCatgCode) => {
    const response = await api.get(`/api/component-category-master/${compCatgCode}`);
    return response.data;
  },
  create: async (data, createdBy = "SYSADMIN001") => {
    const response = await api.post(
      `/api/component-category-master?createdBy=${createdBy}`,
      data
    );
    return response.data;
  },
  update: async (compCatgCode, data, modifiedBy = "SYSADMIN001") => {
    const response = await api.put(
      `/api/component-category-master/${compCatgCode}?modifiedBy=${modifiedBy}`,
      data
    );
    return response.data;
  },
  updateStatus: async (compCatgCode, isActive, modifiedBy = "SYSADMIN001") => {
    const response = await api.patch(
      `/api/component-category-master/${compCatgCode}/status?modifiedBy=${modifiedBy}`,
      { isActive }
    );
    return response.data;
  },
  delete: async (compCatgCode, modifiedBy = "SYSADMIN001") => {
    const response = await api.delete(
      `/api/component-category-master/${compCatgCode}?modifiedBy=${modifiedBy}`
    );
    return response.data;
  },
  getNextCode: async () => {
    const response = await api.get(`/api/component-category-master/next-code`);
    return response.data;
  },
};

// Customer Type API
export const customerTypeAPI = {
  getAll: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.searchText) queryParams.append("searchText", params.searchText);
    if (params.isActive !== undefined)
      queryParams.append("isActive", params.isActive);
    queryParams.append("pageNumber", params.pageNumber || 1);
    queryParams.append("pageSize", params.pageSize || 50);
    const response = await api.get(`/api/customertype?${queryParams}`);
    return response.data;
  },
  getById: async (customerTypeCode) => {
    const response = await api.get(`/api/customertype/${customerTypeCode}`);
    return response.data;
  },
  create: async (data, createdBy = "SYSADMIN001") => {
    const response = await api.post(
      `/api/customertype?createdBy=${createdBy}`,
      data
    );
    return response.data;
  },
  update: async (customerTypeCode, data, modifiedBy = "SYSADMIN001") => {
    const response = await api.put(
      `/api/customertype/${customerTypeCode}?modifiedBy=${modifiedBy}`,
      data
    );
    return response.data;
  },
  delete: async (customerTypeCode, modifiedBy = "SYSADMIN001") => {
    const response = await api.delete(
      `/api/customertype/${customerTypeCode}?modifiedBy=${modifiedBy}`
    );
    return response.data;
  },
};

// Privilege API
export const privilegeAPI = {
  getAll: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.userTypeCode) queryParams.append("userTypeCode", params.userTypeCode);
    if (params.userIdCode) queryParams.append("userIdCode", params.userIdCode);
    if (params.menuCode) queryParams.append("menuCode", params.menuCode);
    if (params.searchText) queryParams.append("searchText", params.searchText);
    if (params.isActive !== undefined) queryParams.append("isActive", params.isActive);
    if (params.viewMode) queryParams.append("viewMode", params.viewMode);
    queryParams.append("pageNumber", params.pageNumber || 1);
    queryParams.append("pageSize", params.pageSize || 50);
    const response = await api.get(`/api/privilege?${queryParams}`);
    return response.data;
  },
  getUserMappings: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.userTypeCode) queryParams.append("userTypeCode", params.userTypeCode);
    if (params.userIdCode) queryParams.append("userIdCode", params.userIdCode);
    if (params.isActive !== undefined && params.isActive !== null) {
      queryParams.append("isActive", params.isActive);
    }
    queryParams.append("pageNumber", params.pageNumber || 1);
    queryParams.append("pageSize", params.pageSize || 50);
    const response = await api.get(`/api/privilege/user-mappings?${queryParams}`);
    return response.data;
  },
  getActiveUserCountByRole: async (userTypeCode) => {
    const response = await api.get(`/api/privilege/user-count/${encodeURIComponent(userTypeCode)}`);
    return response.data;
  },
  updateUserMappingStatus: async (userIdCode, isActive, modifiedBy) => {
    const response = await api.patch(
      `/api/privilege/user-mappings/${encodeURIComponent(userIdCode)}/status?isActive=${isActive}&modifiedBy=${encodeURIComponent(modifiedBy)}`
    );
    return response.data;
  },
  getById: async (privCode) => {
    const response = await api.get(`/api/privilege/${privCode}`);
    return response.data;
  },
  getByUserTypeAndMenu: async (userTypeCode, menuCode) => {
    const response = await api.get(
      `/api/privilege/usertype/${userTypeCode}/menu/${menuCode}`
    );
    return response.data;
  },
  getNextCode: async () => {
    const response = await api.get(`/api/privilege/next-code`);
    return response.data;
  },
  create: async (data) => {
    const response = await api.post(`/api/privilege`, data);
    return response.data;
  },
  update: async (privCode, data) => {
    const response = await api.put(`/api/privilege/${privCode}`, data);
    return response.data;
  },
  delete: async (privCode, modifiedBy) => {
    const response = await api.delete(
      `/api/privilege/${privCode}?modifiedBy=${modifiedBy}`
    );
    return response.data;
  },
  bulkUpdate: async (data) => {
    const response = await api.post(`/api/privilege/bulk-update`, data);
    return response.data;
  },
  getActivityLog: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.actionType) queryParams.append("actionType", params.actionType);
    if (params.targetUserTypeCode) queryParams.append("targetUserTypeCode", params.targetUserTypeCode);
    if (params.targetUserIdCode) queryParams.append("targetUserIdCode", params.targetUserIdCode);
    if (params.searchText) queryParams.append("searchText", params.searchText);
    if (params.fromDate) queryParams.append("fromDate", params.fromDate);
    if (params.toDate) queryParams.append("toDate", params.toDate);
    queryParams.append("pageNumber", params.pageNumber || 1);
    queryParams.append("pageSize", params.pageSize || 50);
    const response = await api.get(`/api/privilege/activity-log?${queryParams}`);
    return response.data;
  },
};

// Workflow API
export const workflowAPI = {
  // Get ticket queries
  getQueries: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.complaintId) queryParams.append("complaintId", params.complaintId);
    if (params.raisedBy) queryParams.append("raisedBy", params.raisedBy);
    if (params.raisedToUserId) queryParams.append("raisedToUserId", params.raisedToUserId);
    if (params.status) queryParams.append("status", params.status);
    if (params.isResolved !== undefined) queryParams.append("isResolved", params.isResolved);
    
    const response = await api.get(`/api/workflow/queries?${queryParams}`);
    return response.data;
  },

  // Get ticket query history by SRCode (+ optional ChildSRCode)
  getTicketQueriesBySR: async ({ srCode, childSrCode } = {}) => {
    const queryParams = new URLSearchParams();
    if (srCode) queryParams.append("srCode", srCode);
    if (childSrCode) queryParams.append("childSrCode", childSrCode);

    const response = await api.get(
      `/api/workflow/ticket-queries/by-sr?${queryParams.toString()}`
    );
    return response.data;
  },

  // Get ticket attachment history by SRCode (+ optional ChildSRCode + optional QueryNo)
  // Note: backend expects QueryNo querystring key casing as `QueryNo`.
  getTicketAttachmentsBySR: async ({ srCode, childSrCode, queryNo } = {}) => {
    const queryParams = new URLSearchParams();
    if (srCode) queryParams.append("srCode", srCode);
    if (childSrCode) queryParams.append("childSrCode", childSrCode);
    if (queryNo) queryParams.append("QueryNo", queryNo);

    const response = await api.get(
      `/api/workflow/ticket-attachments/by-sr?${queryParams.toString()}`
    );
    return response.data;
  },

  // Get down-level users for Raise Query dropdown
  getDownLevelUsersForRaiseQuery: async (userIdCode, childSrCode = null) => {
    const queryParams = new URLSearchParams();
    queryParams.append("userIdCode", userIdCode);
    if (childSrCode) queryParams.append("childSrCode", childSrCode);
    const response = await api.get(
      `/api/workflow/downlevel-users?${queryParams.toString()}`
    );
    return response.data;
  },

  // Raise ticket query (V2 - supports parent + optional child SRCode)
  raiseTicketQueryV2: async (payload, userIdCode) => {
    const response = await api.post(
      `/api/workflow/query/raise-v2?userIdCode=${encodeURIComponent(userIdCode)}`,
      payload
    );
    return response.data;
  },

  // Respond to a query
  // userIdCode: optional legacy query param; prefer payload.submittedBy (same as submit-final).
  respondToQuery: async (queryId, payload, userIdCode) => {
    const code =
      userIdCode != null && String(userIdCode).trim() !== ''
        ? String(userIdCode).trim()
        : '';
    const qs = code ? `?userId=${encodeURIComponent(code)}` : '';
    const response = await api.post(
      `/api/workflow/query/${queryId}/respond${qs}`,
      payload
    );
    return response.data;
  },

  // Create a query (for approvers only, not for SE)
  createQuery: async (request, raisedBy) => {
    const response = await api.post(
      `/api/workflow/query?raisedBy=${raisedBy}`,
      request
    );
    return response.data;
  },

  // Get approval workflow for a complaint
  getApprovalWorkflow: async (complaintId) => {
    const response = await api.get(`/api/workflow/complaint/${complaintId}/workflow`);
    return response.data;
  },

  // Get approval levels for a workflow
  getApprovalLevels: async (workflowId) => {
    const response = await api.get(`/api/workflow/workflow/${workflowId}/levels`);
    return response.data;
  },

  // Get approval history for a complaint
  getApprovalHistory: async (complaintId) => {
    const response = await api.get(`/api/workflow/complaint/${complaintId}/history`);
    return response.data;
  },
};

// Notification API (EmailNotificationLog / EmailNotificationLog table)
export const notificationAPI = {
  getUserCount: async (params = {}) => {
    const qp = new URLSearchParams();
    if (params.recipientUserIdCode)
      qp.append("recipientUserIdCode", params.recipientUserIdCode);
    if (params.fromDate) qp.append("fromDate", params.fromDate);
    if (params.toDate) qp.append("toDate", params.toDate);
    const response = await api.get(`/api/Notification/user/count?${qp.toString()}`);
    return response.data;
  },
  getUserLogs: async (params = {}) => {
    const qp = new URLSearchParams();
    if (params.recipientUserIdCode)
      qp.append("recipientUserIdCode", params.recipientUserIdCode);
    if (params.recipientEmail) qp.append("recipientEmail", params.recipientEmail);
    if (params.fromDate) qp.append("fromDate", params.fromDate);
    if (params.toDate) qp.append("toDate", params.toDate);
    qp.append("pageNumber", params.pageNumber || 1);
    qp.append("pageSize", params.pageSize || 20);
    const response = await api.get(`/api/Notification/user/logs?${qp.toString()}`);
    return response.data;
  },
  markReadAll: async (params = {}) => {
    const qp = new URLSearchParams();
    if (params.recipientUserIdCode)
      qp.append("recipientUserIdCode", params.recipientUserIdCode);
    const response = await api.post(`/api/Notification/user/mark-read-all?${qp.toString()}`);
    return response.data;
  },
};

export const auditAPI = {
  getServiceRequestActivityLogs: async (params = {}) => {
    const qp = new URLSearchParams();
    if (params.fromDate) qp.append("fromDate", params.fromDate);
    if (params.toDate) qp.append("toDate", params.toDate);
    if (params.userIdCode) qp.append("userIdCode", params.userIdCode);
    if (params.pageNumber) qp.append("pageNumber", params.pageNumber);
    if (params.pageSize) qp.append("pageSize", params.pageSize);

    const response = await api.get(`/api/audit/sr-activity-log?${qp.toString()}`);
    return response.data;
  },
};

// Approval Workflow API
// Helper function to get current user's userIdCode from localStorage
const getCurrentUserIdCode = () => {
  try {
    const u = localStorage.getItem("user");
    if (u) {
      const user = JSON.parse(u);
      return user.userIdCode || user.UserIdCode || "SYSADMIN001";
    }
  } catch (e) {
    // Ignore parse errors
  }
  return "SYSADMIN001";
};

export const approvalWorkflowAPI = {
  // Get all workflows
  getAll: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.srTypeCode) queryParams.append("srTypeCode", params.srTypeCode);
    if (params.marketCode) queryParams.append("marketCode", params.marketCode);
    if (params.catgCode) queryParams.append("catgCode", params.catgCode);
    if (params.pageType) queryParams.append("pageType", params.pageType);
    if (params.isActive !== undefined) queryParams.append("isActive", params.isActive);
    
    const url = queryParams.toString() 
      ? `/api/approval-workflow?${queryParams}`
      : `/api/approval-workflow`;
    const response = await api.get(url);
    return response.data;
  },

  // Get single workflow
  get: async (workflowCode) => {
    const response = await api.get(`/api/approval-workflow/${workflowCode}`);
    return response.data;
  },

  // Get next workflow code
  getNextCode: async () => {
    const response = await api.get(`/api/approval-workflow/next-code`);
    return response.data;
  },

  // Create workflow
  create: async (data, createdBy = null) => {
    // Extract createdBy from data if present, otherwise get from localStorage or use parameter
    const { createdBy: dataCreatedBy, ...workflowData } = data;
    const finalCreatedBy = dataCreatedBy || createdBy || getCurrentUserIdCode();
    const response = await api.post(`/api/approval-workflow?createdBy=${finalCreatedBy}`, workflowData);
    return response.data;
  },

  // Update workflow
  update: async (workflowCode, data, modifiedBy = null) => {
    // Extract modifiedBy from data if present, otherwise get from localStorage or use parameter
    const { modifiedBy: dataModifiedBy, createdBy, ...workflowData } = data;
    const finalModifiedBy = dataModifiedBy || modifiedBy || getCurrentUserIdCode();
    const response = await api.put(`/api/approval-workflow/${workflowCode}?modifiedBy=${finalModifiedBy}`, workflowData);
    return response.data;
  },

  // Delete workflow
  delete: async (workflowCode) => {
    const response = await api.delete(`/api/approval-workflow/${workflowCode}`);
    return response.data;
  },

  // Get workflow levels
  getLevels: async (workflowCode) => {
    const response = await api.get(`/api/approval-workflow/${workflowCode}/levels`);
    return response.data;
  },

  // Get single level
  getLevel: async (levelCode) => {
    const response = await api.get(`/api/approval-workflow/level/${levelCode}`);
    return response.data;
  },

  // Create level
  createLevel: async (data, createdBy = null) => {
    // Extract createdBy from data if present, otherwise get from localStorage or use parameter
    const { createdBy: dataCreatedBy, ...levelData } = data;
    const finalCreatedBy = dataCreatedBy || createdBy || getCurrentUserIdCode();
    const response = await api.post(`/api/approval-workflow/level?createdBy=${finalCreatedBy}`, levelData);
    return response.data;
  },

  // Update level
  updateLevel: async (levelCode, data, modifiedBy = null) => {
    // Extract modifiedBy from data if present, otherwise get from localStorage or use parameter
    const { modifiedBy: dataModifiedBy, createdBy, ...levelData } = data;
    const finalModifiedBy = dataModifiedBy || modifiedBy || getCurrentUserIdCode();
    const response = await api.put(`/api/approval-workflow/level/${levelCode}?modifiedBy=${finalModifiedBy}`, levelData);
    return response.data;
  },

  // Delete level
  deleteLevel: async (levelCode) => {
    const response = await api.delete(`/api/approval-workflow/level/${levelCode}`);
    return response.data;
  },

  // Save workflow with levels (combined save)
  saveWorkflowWithLevels: async (workflowData, levelsData, mode = "create", userIdCode = null) => {
    const finalUserIdCode = userIdCode || getCurrentUserIdCode();
    const response = await api.post(
      `/api/approval-workflow/save-with-levels?mode=${mode}&userIdCode=${finalUserIdCode}`,
      {
        workflow: workflowData,
        levels: levelsData,
      }
    );
    return response.data;
  },

  // Get SLA & TAT workflows only (PageType = SLATatConfiguration)
  getAllSlaTat: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.srTypeCode) queryParams.append("srTypeCode", params.srTypeCode);
    if (params.marketCode) queryParams.append("marketCode", params.marketCode);
    if (params.catgCode) queryParams.append("catgCode", params.catgCode);
    if (params.isActive !== undefined && params.isActive !== null) {
      queryParams.append("isActive", params.isActive);
    }
    const response = await api.get(`/api/approval-workflow/sla-tat?${queryParams}`);
    return response.data;
  },
};

// Email Template API
export const emailTemplateAPI = {
  getAll: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.searchText) queryParams.append("searchText", params.searchText);
    if (params.status) queryParams.append("status", params.status);
    if (params.eventType) queryParams.append("eventType", params.eventType);
    queryParams.append("pageNumber", params.pageNumber || 1);
    queryParams.append("pageSize", params.pageSize || 50);
    const response = await api.get(`/api/EmailTemplate?${queryParams}`);
    return response.data;
  },
  searchUsersByEmail: async (searchText) => {
    const queryParams = new URLSearchParams();
    if (searchText) queryParams.append("searchText", searchText);
    const response = await api.get(`/api/Users/search-by-email?${queryParams}`);
    return response.data;
  },
  getByCode: async (templateCode) => {
    const response = await api.get(`/api/EmailTemplate/${templateCode}`);
    return response.data;
  },
  getNextCode: async () => {
    const response = await api.get(`/api/EmailTemplate/next-code`);
    return response.data;
  },
  create: async (data) => {
    const response = await api.post(`/api/EmailTemplate`, data);
    return response.data;
  },
  update: async (templateCode, data) => {
    const response = await api.put(`/api/EmailTemplate/${templateCode}`, data);
    return response.data;
  },
  updateStatus: async (templateCode, isActive) => {
    const response = await api.put(`/api/EmailTemplate/${templateCode}/status`, { isActive });
    return response.data;
  },
  delete: async (templateCode) => {
    const response = await api.delete(`/api/EmailTemplate/${templateCode}`);
    return response.data;
  },
  getConfiguration: async (templateCode) => {
    const response = await api.get(`/api/EmailTemplate/${templateCode}/configuration`);
    return response.data;
  },
  saveConfiguration: async (templateCode, configuration) => {
    const response = await api.put(`/api/EmailTemplate/${templateCode}/configuration`, configuration);
    return response.data;
  },
  getEventTypes: async (isActive = null) => {
    const queryParams = new URLSearchParams();
    if (isActive !== null) queryParams.append("isActive", isActive);
    const response = await api.get(`/api/EmailTemplate/event-types?${queryParams}`);
    return response.data;
  },
};

// SAP Invoice API
export const sapInvoiceAPI = {
  fetchAndSave: async (odnNo) => {
    const response = await api.post("/api/SAPInvoice/fetch", {
      odnno: odnNo,
    });
    return response.data;
  },
  getByOdnNo: async (odnNo) => {
    const response = await api.get(`/api/SAPInvoice/odn/${odnNo}`);
    return response.data;
  },
  getByInvoiceNumber: async (invoiceNumber) => {
    const response = await api.get(`/api/SAPInvoice/invoice/${invoiceNumber}`);
    return response.data;
  },
};

export default api;
